import { RouteObject, createBrowserRouter } from "react-router-dom"
import { AppLayout } from "../pages/layout.app"
import { NotFound } from "../pages/NotFound"

export const ALL_ROUTES = {
   basePath: '/',
   percorso: '/percorso',
   atleti: '/atleti',
   atleteDetail: '/atleti/:id',
   artworks: '/artworks',
   artworkDetail: '/artworks/:id',
   discipline: '/discipline',
   navigaNelTempo: '/naviga-nel-tempo',
   illustratrice: '/illustratrice',
   giornalista: '/giornalista',
   infografiche: '/infografiche',
   tuttiGliAtleti: '/tutti-gli-atleti',
   tuttiGliAtletiDetail: '/tutti-gli-atleti/:id',
   quiz: '/quiz',
   crediti: '/crediti',
   accessibilita: '/accessibilita',
}

/**
 * Mapping tra percorsi e chiavi i18n dei titoli della pagina
 * Usato dalla Navbar per mostrare il titolo dinamico (tradotto in tempo reale)
 * Le chiavi vengono tradotte da AppLayout con useTranslation()
 */
export const PAGE_TITLES: Record<string, string> = {
   [ALL_ROUTES.basePath]: '',
   [ALL_ROUTES.percorso]: 'pages.navTitles.percorsi',
   [ALL_ROUTES.atleti]: 'pages.navTitles.atleti',
   [ALL_ROUTES.discipline]: 'pages.navTitles.discipline',
   [ALL_ROUTES.navigaNelTempo]: 'pages.navTitles.navigaNelTempo',
   [ALL_ROUTES.illustratrice]: 'pages.navTitles.illustratrice',
   [ALL_ROUTES.giornalista]: 'pages.navTitles.giornalista',
   [ALL_ROUTES.infografiche]: 'pages.navTitles.infografiche',
   [ALL_ROUTES.tuttiGliAtleti]: 'pages.navTitles.tuttiGliAtleti',
   [ALL_ROUTES.quiz]: 'pages.navTitles.quiz',
   [ALL_ROUTES.crediti]: 'pages.navTitles.crediti',
   [ALL_ROUTES.accessibilita]: 'pages.navTitles.accessibilita',
}

/**
 * Ottiene la chiave i18n del titolo della pagina dal percorso corrente
 * @param pathname - Il percorso dalla Location
 * @returns La chiave i18n del titolo (es. 'pages.navTitles.atleti'), o empty string se non trovato
 *
 * Nota: La chiave deve essere tradotta con useTranslation() per ottenere il titolo localizzato
 * Vedi AppLayout.tsx per l'utilizzo
 */
export function getTitleFromPath(pathname: string): string {
   return PAGE_TITLES[pathname] || ''
}


export const globalRoutes: RouteObject[] = [
   {
      element: <AppLayout />,
      children: [
         {
            path: ALL_ROUTES.basePath,
            async lazy() {
               let { HomePage } = await import("../pages/HomePage/HomePage")
               return { Component: HomePage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.percorso,
            async lazy() {
               let { PercorsiPage } = await import("../pages/PercorsiPage/PercorsiPage")
               return { Component: PercorsiPage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.atleti,
            async lazy() {
               let { AtletiPage } = await import("../pages/AtletiPage/AtletiPage")
               return { Component: AtletiPage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.atleteDetail,
            async lazy() {
               let { AthleteDetailPage } = await import("../pages/AthleteDetailPage/AthleteDetailPage")
               return { Component: AthleteDetailPage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.artworkDetail,
            async lazy() {
               let { ArtworkDetailPage } = await import("../pages/ArtworkDetailPage/ArtworkDetailPage")
               return { Component: ArtworkDetailPage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.discipline,
            async lazy() {
               let { DisciplinesPage } = await import("../pages/DisciplinesPage/DisciplinesPage")
               return { Component: DisciplinesPage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.navigaNelTempo,
            async lazy() {
               let { OlympicTimelinePage } = await import("../pages/OlympicTimelinePage/OlympicTimelinePage")
               return { Component: OlympicTimelinePage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.illustratrice,
            async lazy() {
               let { IllustratorePage } = await import("../pages/IllustratorePage/IllustratorePage")
               return { Component: IllustratorePage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.giornalista,
            async lazy() {
               let { JournalistPage } = await import("../pages/JournalistPage/JournalistPage")
               return { Component: JournalistPage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.crediti,
            async lazy() {
               let { CreditsPage } = await import("../pages/CreditsPage/CreditsPage")
               return { Component: CreditsPage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.accessibilita,
            async lazy() {
               let { AccessibilityPage } = await import("../pages/AccessibilityPage/AccessibilityPage")
               return { Component: AccessibilityPage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.infografiche,
            async lazy() {
               let { InfografichePage } = await import("../pages/InfografichePage/InfografichePage")
               return { Component: InfografichePage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.tuttiGliAtleti,
            async lazy() {
               let { AllAthletesPage } = await import("../pages/AllAthletesPage/AllAthletesPage")
               return { Component: AllAthletesPage }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: ALL_ROUTES.tuttiGliAtletiDetail,
            async lazy() {
               let { AthleteDetailPage } = await import("../pages/AthleteDetailPage/AthleteDetailPage")
               return {
                  Component: () => <AthleteDetailPage mode="detail" />
               }
            },
            HydrateFallback: () => <div>Loading...</div>
         },
         {
            path: "*",
            element: <NotFound />
         }
      ]
   }
]

export const Router = createBrowserRouter(globalRoutes, {
  future: {
    v7_partialHydration: true,
  },
})
